import React, { useEffect, useMemo, useState } from "react";
import { motion, useAnimation } from "framer-motion";

/**
 * SpinWheel
 * props:
 *   segments: [{ id, label, color, type, value, weight }]
 *   spinning: bool (parent triggers)
 *   targetIndex: int | null  (which slice to land on; pointer at top)
 *   onDone: () => void
 *   size: number (px)
 */
export default function SpinWheel({ segments, spinning, targetIndex, onDone, size = 320 }) {
  const controls = useAnimation();
  const [rotation, setRotation] = useState(0);

  const n = segments.length;
  const sliceAngle = 360 / Math.max(n, 1);

  useEffect(() => {
    if (spinning && targetIndex != null && n > 0) {
      // Pointer is at top (12 o'clock). To land on segment i (whose center is at i*sliceAngle from 0deg start),
      // we want segment center to align with -90deg (top) after rotation.
      // segment center angle = i*sliceAngle + sliceAngle/2 (clockwise from "3 o'clock" which is 0deg in our SVG)
      // We orient the wheel so that 0deg points to the top. Achieve by rotating the wheel such that the chosen slice midpoint reaches 0deg pointer position.
      const segCenter = targetIndex * sliceAngle + sliceAngle / 2;
      // We want final rotation R so that (segCenter + R) % 360 === 0 (top). So R = -segCenter mod 360.
      const baseTarget = (360 - segCenter) % 360;
      const fullSpins = 6 * 360; // 6 full spins
      const finalRotation = rotation + fullSpins + (baseTarget - (rotation % 360) + 360) % 360;
      setRotation(finalRotation);
      controls.start({
        rotate: finalRotation,
        transition: { duration: 4.2, ease: [0.17, 0.67, 0.32, 1.0] },
      }).then(() => { onDone && onDone(); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinning, targetIndex]);

  // build SVG paths
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 6;

  const slices = useMemo(() => {
    const arr = [];
    for (let i = 0; i < n; i++) {
      const startAngle = i * sliceAngle;
      const endAngle = startAngle + sliceAngle;
      const a1 = (startAngle - 90) * Math.PI / 180;
      const a2 = (endAngle - 90) * Math.PI / 180;
      // We compute paths assuming pointer at top, slice 0 starts at top going clockwise.
      // To match the rotation logic above (segCenter measured from "0deg = top"), we offset by -90 here.
      const x1 = cx + r * Math.cos(a1);
      const y1 = cy + r * Math.sin(a1);
      const x2 = cx + r * Math.cos(a2);
      const y2 = cy + r * Math.sin(a2);
      const large = sliceAngle > 180 ? 1 : 0;
      const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`;
      // label position
      const midA = (startAngle + sliceAngle / 2 - 90) * Math.PI / 180;
      const lr = r * 0.62;
      const lx = cx + lr * Math.cos(midA);
      const ly = cy + lr * Math.sin(midA);
      const labelRotation = startAngle + sliceAngle / 2;
      arr.push({ d, lx, ly, labelRotation, color: segments[i].color || "#FF7A59", label: segments[i].label });
    }
    return arr;
  }, [n, sliceAngle, segments, cx, cy, r]);

  if (n === 0) return null;

  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="spin-wheel">
      {/* Pointer */}
      <div
        className="absolute left-1/2 -translate-x-1/2 z-10"
        style={{ top: -6 }}
      >
        <div className="wheel-pointer" />
      </div>

      <motion.svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        animate={controls}
        style={{ transformOrigin: "50% 50%", filter: "drop-shadow(4px 4px 0 #1A1A1A)" }}
      >
        <circle cx={cx} cy={cy} r={r + 3} fill="#1A1A1A" />
        {slices.map((s, i) => (
          <g key={i}>
            <path d={s.d} fill={s.color} stroke="#1A1A1A" strokeWidth="1.5" />
            <text
              x={s.lx}
              y={s.ly}
              fill="#1A1A1A"
              fontSize={Math.max(11, size / 26)}
              fontWeight="800"
              textAnchor="middle"
              dominantBaseline="middle"
              transform={`rotate(${s.labelRotation}, ${s.lx}, ${s.ly})`}
              style={{ fontFamily: "Cabinet Grotesk, sans-serif", pointerEvents: "none" }}
            >
              {(s.label || "").slice(0, 14)}
            </text>
          </g>
        ))}
        {/* Center hub */}
        <circle cx={cx} cy={cy} r={size * 0.08} fill="#fff" stroke="#1A1A1A" strokeWidth="2" />
        <circle cx={cx} cy={cy} r={size * 0.04} fill="#1A1A1A" />
      </motion.svg>
    </div>
  );
}
